import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ForwardedRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type Ref,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.ts'

export interface ComboboxOption {
  id: string
  label: string
  /** Hex color shown as a small dot. Omitted or null → no dot rendered. */
  color?: string | null
}

export interface MultiComboboxProps {
  /** Full list of available options (pre-loaded; the component filters client-side). */
  options: ComboboxOption[]
  /** Currently selected option ids. */
  value: string[]
  onChange: (ids: string[]) => void
  /** When provided, the "Create '…'" row appears for unmatched queries. */
  onCreateOption?: (name: string) => Promise<ComboboxOption>
  placeholder?: string
  /** Shown when no options match and create is not available. */
  noOptionsLabel?: string
  /** Returns the label text for the create row. Consumer passes a localized fn. */
  createLabel?: (name: string) => string
  disabled?: boolean
  autoFocus?: boolean
  className?: string
}

interface Coords {
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

const LISTBOX_GAP = 4
const LISTBOX_MAX_HEIGHT = 240
const LISTBOX_MIN_HEIGHT = 120
const CREATE_ID = '__create__'

function MultiComboboxInner(
  {
    options,
    value,
    onChange,
    onCreateOption,
    placeholder,
    noOptionsLabel,
    createLabel,
    disabled,
    autoFocus,
    className,
  }: MultiComboboxProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [focusedIdx, setFocusedIdx] = useState(-1)
  const [creating, setCreating] = useState(false)
  const [coords, setCoords] = useState<Coords | null>(null)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Visible dropdown items — exclude already-selected, filter by query.
  const filtered = options
    .filter((o) => !value.includes(o.id))
    .filter((o) => !query || o.label.toLowerCase().includes(query.toLowerCase()))

  const hasExactMatch =
    filtered.some((o) => o.label.toLowerCase() === query.toLowerCase()) ||
    value.some(
      (id) => options.find((o) => o.id === id)?.label.toLowerCase() === query.toLowerCase(),
    )

  const showCreate = query.trim().length > 0 && !hasExactMatch && !!onCreateOption && !creating

  const itemIds: string[] = [...filtered.map((o) => o.id), ...(showCreate ? [CREATE_ID] : [])]
  const totalItems = itemIds.length

  // Reset the focused item whenever the visible list changes (query filtering or value change).
  useEffect(() => {
    setFocusedIdx(totalItems > 0 ? 0 : -1)
  }, [totalItems])

  // Measure and position the portal dropdown.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    const measure = () => {
      const node = containerRef.current
      if (!node) return
      const r = node.getBoundingClientRect()
      const spaceBelow = window.innerHeight - r.bottom - LISTBOX_GAP
      const spaceAbove = r.top - LISTBOX_GAP
      const openUp = spaceBelow < LISTBOX_MAX_HEIGHT && spaceAbove > spaceBelow
      const space = openUp ? spaceAbove : spaceBelow
      const maxHeight = Math.max(LISTBOX_MIN_HEIGHT, Math.min(LISTBOX_MAX_HEIGHT, space))
      setCoords({
        ...(openUp
          ? { bottom: window.innerHeight - r.top + LISTBOX_GAP }
          : { top: r.bottom + LISTBOX_GAP }),
        left: r.left,
        width: r.width,
        maxHeight,
      })
    }
    measure()
    const onScroll = (e: Event) => {
      // Scroll inside the dropdown itself (user scrolling the option list) must not close it.
      const t = e.target as Node | null
      if (dropdownRef.current && t && dropdownRef.current.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', measure)
    }
  }, [open])

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node | null
      if (!t) return
      if (containerRef.current?.contains(t)) return
      if (dropdownRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  // Scroll the focused option into view when navigating by keyboard.
  useEffect(() => {
    if (!open || focusedIdx < 0 || !dropdownRef.current) return
    const items = dropdownRef.current.querySelectorAll('[role="option"]')
    items[focusedIdx]?.scrollIntoView({ block: 'nearest' })
  }, [focusedIdx, open])

  function addOption(id: string) {
    if (!value.includes(id)) onChange([...value, id])
    setQuery('')
    inputRef.current?.focus()
  }

  function removeOption(id: string) {
    onChange(value.filter((v) => v !== id))
  }

  async function handleCreate() {
    if (!onCreateOption || !query.trim() || creating) return
    setCreating(true)
    try {
      const created = await onCreateOption(query.trim())
      onChange([...value, created.id])
      setQuery('')
    } catch {
      // Error surface is the caller's responsibility (via onCreateOption throwing).
      // MultiCombobox only resets its own creating state so the user can retry.
    } finally {
      setCreating(false)
      inputRef.current?.focus()
    }
  }

  function activateFocused() {
    if (focusedIdx < 0 || focusedIdx >= totalItems) return
    const id = itemIds[focusedIdx]
    if (id === CREATE_ID) {
      void handleCreate()
    } else {
      addOption(id)
    }
  }

  function onInputKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          setFocusedIdx(0)
          return
        }
        setFocusedIdx((prev) => (prev < totalItems - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (!open) return
        setFocusedIdx((prev) => (prev > 0 ? prev - 1 : totalItems - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          return
        }
        activateFocused()
        break
      case 'Escape':
        setOpen(false)
        setFocusedIdx(-1)
        break
      case 'Backspace':
        if (!query && value.length > 0) {
          removeOption(value[value.length - 1])
        }
        break
    }
  }

  const dropdownStyle: CSSProperties =
    coords !== null
      ? {
          position: 'fixed',
          ...(coords.bottom !== undefined ? { bottom: coords.bottom } : { top: coords.top }),
          left: coords.left,
          width: coords.width,
          maxHeight: coords.maxHeight,
        }
      : { position: 'fixed', visibility: 'hidden' }

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Chips + search input row */}
      <div
        className={cn(
          'flex min-h-9 w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-border bg-input px-2 py-1.5',
          'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {value.map((id) => {
          const opt = options.find((o) => o.id === id)
          return (
            <span
              key={id}
              className="flex items-center gap-1 rounded-md border border-border/60 bg-background-secondary/60 px-1.5 py-0.5 text-[11px] text-foreground"
            >
              {opt?.color && (
                <span
                  className="inline-block size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: opt.color }}
                />
              )}
              <span>{opt?.label ?? id}</span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${opt?.label ?? id}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    removeOption(id)
                  }}
                  className="ml-0.5 rounded text-muted outline-none hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
                >
                  ×
                </button>
              )}
            </span>
          )
        })}
        <input
          ref={(node) => {
            inputRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) (ref as { current: HTMLInputElement | null }).current = node
          }}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={open && focusedIdx >= 0 ? `mcb-option-${focusedIdx}` : undefined}
          disabled={disabled}
          autoFocus={autoFocus}
          value={query}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKeyDown}
          className="min-w-24 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted outline-none disabled:cursor-not-allowed"
        />
      </div>

      {/* Portal-rendered dropdown */}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            tabIndex={-1}
            style={dropdownStyle}
            className="z-50 overflow-y-auto rounded-md border border-border bg-background-app py-1 shadow-lg outline-none"
          >
            {filtered.map((opt, i) => (
              <button
                key={opt.id}
                id={`mcb-option-${i}`}
                type="button"
                role="option"
                aria-selected={false}
                // mouseDown + preventDefault keeps focus on the input
                onMouseDown={(e) => {
                  e.preventDefault()
                  addOption(opt.id)
                }}
                onMouseEnter={() => setFocusedIdx(i)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors outline-none',
                  focusedIdx === i
                    ? 'bg-sidebar-hover-bg text-foreground'
                    : 'text-foreground hover:bg-sidebar-hover-bg',
                )}
              >
                {opt.color && (
                  <span
                    className="inline-block size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: opt.color }}
                  />
                )}
                {opt.label}
              </button>
            ))}

            {showCreate && (
              <button
                id={`mcb-option-${filtered.length}`}
                type="button"
                role="option"
                aria-selected={false}
                onMouseDown={(e) => {
                  e.preventDefault()
                  void handleCreate()
                }}
                onMouseEnter={() => setFocusedIdx(filtered.length)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm italic transition-colors outline-none',
                  focusedIdx === filtered.length
                    ? 'bg-sidebar-hover-bg text-foreground'
                    : 'text-muted hover:bg-sidebar-hover-bg hover:text-foreground',
                )}
              >
                {createLabel ? createLabel(query.trim()) : `Create "${query.trim()}"`}
              </button>
            )}

            {totalItems === 0 && (
              <p className="px-3 py-2 text-sm text-muted">{noOptionsLabel ?? 'No options'}</p>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}

/*
 * forwardRef over a generic-ish component. The ref exposes the search input,
 * letting consumers focus it programmatically (e.g., on inline-edit open).
 */
export const MultiCombobox = forwardRef(MultiComboboxInner) as (
  props: MultiComboboxProps & { ref?: Ref<HTMLInputElement> },
) => ReactElement
